import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface Slot {
  start: string;
  end: string;
}

interface BookingInfo {
  valid: boolean;
  opponentName: string;
  leagueName: string | null;
  legs: number;
  ownerName: string;
  ownerTimezone: string;
  expiresAt: string;
  availableSlots: Slot[];
}

export default function PublicBook() {
  const { token } = useParams<{ token: string }>();
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [notes, setNotes] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  useEffect(() => {
    const fetchBookingInfo = async () => {
      if (!token) return;

      try {
        const response = await api.get(`/book/${token}`);
        setBookingInfo(response.data);
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string; expired?: boolean; used?: boolean } } };
        if (error.response?.data?.expired) {
          setError('This booking link has expired.');
        } else if (error.response?.data?.used) {
          setError('This booking link has already been used.');
        } else {
          setError('Invalid booking link.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingInfo();
  }, [token]);

  const handleBook = async () => {
    if (!selectedSlot || !token) return;

    setIsBooking(true);
    setError('');

    try {
      await api.post(`/book/${token}`, {
        slotStart: selectedSlot.start,
        notes: notes || undefined
      });
      setBookingComplete(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to book. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#101022]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#101022] px-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-4xl mb-4">:(</div>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (bookingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#101022] px-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-4xl mb-4">🎯</div>
          <h1 className="text-xl font-bold text-green-400 mb-2">Game Booked!</h1>
          <p className="text-gray-400">
            Your game has been scheduled. {bookingInfo?.ownerName} will be notified.
          </p>
          {selectedSlot && (
            <div className="mt-4 p-4 bg-[#252550] rounded-lg">
              <p className="font-medium text-white">
                {format(parseISO(selectedSlot.start), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-gray-400">
                {format(parseISO(selectedSlot.start), 'h:mm a')} - {format(parseISO(selectedSlot.end), 'h:mm a')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!bookingInfo) return null;

  // Group slots by date
  const slotsByDate: Record<string, Slot[]> = {};
  bookingInfo.availableSlots.forEach((slot) => {
    const dateKey = format(parseISO(slot.start), 'yyyy-MM-dd');
    if (!slotsByDate[dateKey]) {
      slotsByDate[dateKey] = [];
    }
    slotsByDate[dateKey].push(slot);
  });

  return (
    <div className="min-h-screen bg-[#101022] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="card mb-6">
          <h1 className="text-2xl font-bold text-center text-[#1111d4] mb-2">
            Book a Game
          </h1>
          <p className="text-center text-gray-400">
            Schedule your match with <strong className="text-white">{bookingInfo.ownerName}</strong>
          </p>
          <div className="mt-4 text-center text-sm text-gray-500">
            <p>{bookingInfo.leagueName || 'Casual Game'} - {bookingInfo.legs} legs</p>
            <p>Playing as: {bookingInfo.opponentName}</p>
          </div>
        </div>

        {/* Slot Selection */}
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Select a Time Slot</h2>

          {Object.keys(slotsByDate).length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No available slots. Please contact {bookingInfo.ownerName} directly.
            </p>
          ) : (
            <div className="space-y-4">
              {Object.entries(slotsByDate).map(([dateKey, slots]) => (
                <div key={dateKey}>
                  <h3 className="font-medium text-gray-300 mb-2">
                    {format(parseISO(dateKey), 'EEEE, MMMM d')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot.start}
                        onClick={() => setSelectedSlot(slot)}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedSlot?.start === slot.start
                            ? 'bg-[#1111d4] text-white'
                            : 'bg-[#252550] hover:bg-[#2a2a5a] text-gray-300'
                        }`}
                      >
                        {format(parseISO(slot.start), 'h:mm a')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes & Confirm */}
        {selectedSlot && (
          <div className="card">
            <h2 className="text-lg font-semibold text-white mb-4">Confirm Booking</h2>

            <div className="p-4 bg-[#1111d4]/20 border border-[#1111d4]/30 rounded-lg mb-4">
              <p className="font-medium text-[#1111d4]">
                {format(parseISO(selectedSlot.start), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-[#1111d4]/80">
                {format(parseISO(selectedSlot.start), 'h:mm a')} - {format(parseISO(selectedSlot.end), 'h:mm a')}
              </p>
            </div>

            <div className="mb-4">
              <label className="label">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="input"
                rows={3}
                placeholder="Any message for your opponent..."
                maxLength={500}
              />
            </div>

            <button
              onClick={handleBook}
              disabled={isBooking}
              className="btn-primary w-full"
            >
              {isBooking ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-600 mt-6">
          Powered by Darts Scheduler
        </div>
      </div>
    </div>
  );
}
